import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  DataTable,
  ExportCsvButton,
  FilterBar,
  SearchInput,
  Section,
  SelectFilter,
  StatCard,
} from "@/components/admin/admin-kit";
import { admListServices, admModerateService, admUpdateCreatorService } from "@/lib/admin-pro.functions";
import type { creatorServiceUpdateSchema, serviceModerateSchema } from "@/lib/admin-pro.schemas";
import type { z } from "zod";
import { formatMoney } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/services")({
  head: () => privatePageMetadata("/nc/live/adm/services", "Services | Control room", "Internal service moderation."),
  component: AdminServices,
});

const STATUS = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "pending_moderation", label: "Pending moderation" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "rejected", label: "Rejected" },
] as const;

const MODERATION = [
  { value: "all", label: "All moderation" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "flagged", label: "Flagged" },
] as const;

function AdminServices() {
  const run = useServerFn(admListServices);
  const moderate = useServerFn(admModerateService);
  const updateCreator = useServerFn(admUpdateCreatorService);
  const qc = useQueryClient();

  const [status, setStatus] = useState<string>("all");
  const [moderation, setModeration] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["adm-services", status, moderation, search],
    queryFn: () => run({ data: { status, moderation, search: search || undefined, limit: 100 } }),
  });

  const mod = useMutation({
    mutationFn: (input: z.infer<typeof serviceModerateSchema>) => moderate({ data: input }),
    onSuccess: () => {
      toast.success("Service updated");
      void qc.invalidateQueries({ queryKey: ["adm-services"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const creator = useMutation({
    mutationFn: (input: z.infer<typeof creatorServiceUpdateSchema>) => updateCreator({ data: input }),
    onSuccess: () => {
      toast.success("Creator service updated");
      void qc.invalidateQueries({ queryKey: ["adm-services"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const services = data?.services ?? [];

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard label="Listed" value={services.length} />
        <StatCard
          label="Pending moderation"
          value={services.filter((s) => s.moderation_status === "pending").length}
          tone="warn"
        />
        <StatCard label="Active" value={services.filter((s) => s.status === "active").length} tone="ok" />
        <StatCard label="Ambassador picks" value={services.filter((s) => s.is_ambassador_pick).length} tone="hot" />
      </div>

      <Section
        title="Marketplace services"
        description="Moderate, publish, pause, flag and feature every seller service."
        actions={<ExportCsvButton rows={services as unknown as Record<string, unknown>[]} filename="behot-services" />}
      >
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Title or slug…" />
          <SelectFilter label="Status" value={status} onChange={setStatus} options={STATUS} />
          <SelectFilter label="Moderation" value={moderation} onChange={setModeration} options={MODERATION} />
        </FilterBar>

        <DataTable
          rows={services}
          rowKey={(r) => r.id}
          empty={isLoading ? "Loading…" : "No services for this filter."}
          columns={[
            {
              key: "title",
              header: "Service",
              cell: (r) => (
                <div>
                  <p className="font-medium">{r.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.seller?.display_name ?? r.seller_user_id.slice(0, 8)} · {formatMoney(r.starting_price_cents ?? 0)}
                  </p>
                </div>
              ),
              sortValue: (r) => r.title,
            },
            {
              key: "status",
              header: "Status",
              cell: (r) => (
                <div className="flex flex-col gap-1">
                  <Badge variant="outline">{r.status}</Badge>
                  <Badge variant={r.moderation_status === "approved" ? "default" : "secondary"}>
                    {r.moderation_status}
                  </Badge>
                </div>
              ),
              sortValue: (r) => String(r.status),
            },
            {
              key: "metrics",
              header: "Metrics",
              cell: (r) => (
                <span className="text-xs text-muted-foreground">
                  {Number(r.orders_count ?? 0)} orders · {Number(r.view_count ?? 0)} views ·{" "}
                  {Number(r.rating_avg ?? 0).toFixed(1)}★ ({Number(r.rating_count ?? 0)})
                </span>
              ),
              sortValue: (r) => Number(r.orders_count ?? 0),
            },
            {
              key: "actions",
              header: "Moderation",
              className: "min-w-[300px]",
              cell: (r) => (
                <div className="space-y-2">
                  <Textarea
                    rows={2}
                    placeholder="Moderation note (sent to the audit log)"
                    value={notes[r.id] ?? r.moderation_notes ?? ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      size="sm"
                      variant="hot"
                      disabled={mod.isPending}
                      onClick={() =>
                        mod.mutate({
                          serviceId: r.id,
                          moderationStatus: "approved",
                          status: "active",
                          notes: notes[r.id] || undefined,
                        })
                      }
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={mod.isPending}
                      onClick={() =>
                        mod.mutate({
                          serviceId: r.id,
                          moderationStatus: "rejected",
                          status: "rejected",
                          notes: notes[r.id] || undefined,
                        })
                      }
                    >
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={mod.isPending}
                      onClick={() => mod.mutate({ serviceId: r.id, status: "paused", notes: notes[r.id] || undefined })}
                    >
                      Pause
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={mod.isPending}
                      onClick={() =>
                        mod.mutate({ serviceId: r.id, moderationStatus: "flagged", notes: notes[r.id] || undefined })
                      }
                    >
                      Flag
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={mod.isPending}
                      onClick={() => mod.mutate({ serviceId: r.id, isAmbassadorPick: !r.is_ambassador_pick })}
                    >
                      {r.is_ambassador_pick ? "Unpick" : "Ambassador pick"}
                    </Button>
                  </div>
                </div>
              ),
            },
          ]}
        />
      </Section>

      <Section title="Creator services" description="Promotion services offered by creators on their profiles.">
        <DataTable
          rows={data?.creatorServices ?? []}
          rowKey={(r) => r.id}
          empty="No creator services yet."
          columns={[
            {
              key: "title",
              header: "Service",
              cell: (r) => (
                <div>
                  <p className="font-medium">{String(r.title ?? r.kind ?? "Service")}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.profile?.display_name ?? String(r.user_id).slice(0, 8)}
                  </p>
                </div>
              ),
            },
            {
              key: "price",
              header: "Price",
              cell: (r) => formatMoney(Number(r.price_cents ?? 0)),
              sortValue: (r) => Number(r.price_cents ?? 0),
            },
            {
              key: "active",
              header: "Active",
              cell: (r) => (
                <Button
                  size="sm"
                  variant={r.is_active ? "outline" : "hot"}
                  disabled={creator.isPending}
                  onClick={() => creator.mutate({ serviceId: r.id, isActive: !r.is_active })}
                >
                  {r.is_active ? "Disable" : "Enable"}
                </Button>
              ),
            },
          ]}
        />
      </Section>
    </div>
  );
}
